import type {
  AIProvider,
  AIProviderInput,
  AIValidation,
  AIValidationErrorCode,
  AIVendor
} from "@/api/useTenant.types"
import type { TFunction } from "@/hooks/useTranslation"
import type { AIProviderFormValues } from "./aiProvider.types"

export const AI_VENDORS: AIVendor[] = ["gcp_agent_platform"]

// Only global serves every model Kai uses (checked 2026-10-29); add regions here as Google serves them
export const AGENT_PLATFORM_REGIONS = ["global"]

const AI_VENDOR_DEFAULTS: Record<
  AIVendor,
  Pick<AIProviderFormValues, "region">
> = {
  gcp_agent_platform: { region: "global" },
  aws_bedrock: { region: "" }
}

const ERROR_CODES: AIValidationErrorCode[] = [
  "invalid_input",
  "auth_failed",
  "api_disabled",
  "permission_denied",
  "model_not_in_region",
  "quota_exceeded",
  "credentials_unreadable",
  "unavailable"
]

export const isAIProviderValid = (provider?: AIProvider) =>
  provider?.validation.status === "valid"

// The vendor-specific settings sent besides region.
const AI_VENDOR_FIELDS: Record<AIVendor, ("projectId" | "accessKeyId")[]> = {
  gcp_agent_platform: ["projectId"],
  aws_bedrock: ["accessKeyId"]
}

// The stored secret belongs to the stored vendor and access key ID; changing either needs a new one.
export const hasStoredSecret = (
  values: Pick<AIProviderFormValues, "vendor" | "accessKeyId">,
  provider?: AIProvider
) =>
  !!provider?.credentialsSet &&
  provider.vendor === values.vendor &&
  (!AI_VENDOR_FIELDS[values.vendor].includes("accessKeyId") ||
    (values.accessKeyId?.trim() || "") === (provider.accessKeyId || ""))

export const isValidJson = (value: string) => {
  try {
    JSON.parse(value)

    return true
  } catch {
    return false
  }
}

// Switching vendor starts from that vendor's defaults; switching back restores the saved settings.
export const getAIProviderFormValues = (
  provider?: AIProvider,
  vendor: AIVendor = provider?.vendor || "gcp_agent_platform"
): AIProviderFormValues => {
  if (!provider?.configured || provider.vendor !== vendor)
    return { vendor, ...AI_VENDOR_DEFAULTS[vendor] }

  return {
    vendor,
    region: provider.region,
    projectId: provider.projectId ?? undefined,
    accessKeyId: provider.accessKeyId ?? undefined
  }
}

export const toAIProviderInput = (
  values: AIProviderFormValues
): AIProviderInput => {
  const fields = AI_VENDOR_FIELDS[values.vendor]

  return {
    vendor: values.vendor,
    region: values.region.trim(),
    projectId: fields.includes("projectId")
      ? values.projectId?.trim()
      : undefined,
    accessKeyId: fields.includes("accessKeyId")
      ? values.accessKeyId?.trim()
      : undefined,
    secret: values.secret?.trim() || undefined
  }
}

// A vendor-neutral title per code; the backend's message carries the vendor-specific fix.
export const getValidationText = (validation: AIValidation, t: TFunction) => {
  const { errorCode, modelId } = validation
  const detail = validation.message ?? ""

  if (!errorCode || !ERROR_CODES.includes(errorCode))
    return { title: t("aiProvider.errors.unknown"), detail }
  if (errorCode === "model_not_in_region" && modelId)
    return {
      title: t("aiProvider.errors.modelNotInRegion", { modelId }),
      detail
    }

  return { title: t(`aiProvider.errors.${errorCode}`), detail }
}
